import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Checkbox, Input, Tabs } from "antd";
import { useLocation } from "react-router-dom";

import { runAutoCalculation } from "../utils/calculatorUtils";
import Description from "./description";
import Header from "./header";
import "./index.css";
import TestMethods from "./TestMethods";

// SAM metadata provenance: local helpers; no new application module.
const samCalculatorMetadataKeys = ["calculatorName", "inputParameters", "calculatorVersion"];
// Hooke's Law is identified semantically, not by a single API spelling.
const isSamHookesLawField = (key, field = {}) => [key, field.propertyMetaData?.dbColumnName,
  field.propertyMetaData?.fieldLabel, field.propertyMetaData?.label].some((value) =>
  /^hooke?s?law(?:constant)?$/.test(samNormalizeKey(value).replace(/^sam/, "")));
const isSamCalculatorDetailField = (key) => isSamHookesLawField(key) ||
  ["loadcoefficients", "loadcoefficientsa", "loadcoefficientsb"].includes(samNormalizeKey(key));
const usesSamCalculatorDetails = (key, metadata = {}) => isSamCalculatorDetailField(key) ||
  ["gelpropa", "gelpropb"].includes(samNormalizeKey(metadata.calculatorName));
const samMetadataKeys = ["source", "author", "testMethod", "creationDate", "repCount", "sampleId", "requestDate", "description"];
const samClone = (value) => {
  if (value instanceof Date) return new Date(value.getTime());
  if (Array.isArray(value)) return value.map(samClone);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, samClone(item)]));
  return value;
};
const samObject = (value) => {
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return {}; }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? samClone(value) : {};
};
const samMissing = (value) => value === undefined || value === null || value === "" || value === "-";
const samNormalizeKey = (value) => String(value || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
const manualSamFieldKeys = new Set([
  "drygeldensity", "solidsdrygeldensity",
  "drygelradius", "avgdrygelradius", "solidsdrygelradius",
  "swellingcoefficients", "swellingcoefficientsk", "swellingcoefficientsn",
  "samcapacity03", "samcapacity03psi", "samcapacityat03psi", "maxcapacity",
  "geldiffusivity", "hookeslaw", "hookeslawconstant",
  "gelosmoticactivity", "contactangle", "samcontactangle",
  "loadcoefficients", "loadcoefficientsa", "loadcoefficientsb",
  "1secondaul", "1secaul", "vortextime", "gelbedpermeability",
  "freeswellcapacity", "moisture", "03psigbp", "gbp03psi", "gbp", "samcrc",
]);
const isManualSamField = (key, field = {}) => {
  const candidates = [
    key,
    field.propertyMetaData?.dbColumnName,
    field.propertyMetaData?.fieldLabel,
    field.propertyMetaData?.label,
  ].map(samNormalizeKey);
  return candidates.some((candidate) => manualSamFieldKeys.has(candidate) ||
    candidate.includes("geldiffusivity") || candidate.includes("hookeslaw") ||
    candidate.includes("gelosmoticactivity") || candidate.includes("contactangle"));
};
const canonicalSamMetadata = (value) => {
  const result = samObject(value);
  const aliases = {
    calculatorName: ["calculatorname", "calcname"],
    inputParameters: ["inputparameters"],
    calculatorVersion: ["calculatorversion"],
    source: ["source"], author: ["author", "createdby", "requestorname", "requestor"],
    testMethod: ["testmethod", "testmethodname", "methodname", "methodid", "method"],
    creationDate: ["creationdate", "completeddt", "completeddate", "completiondate", "createddate", "createdt"],
    repCount: ["repcount", "replicateid", "replicatecount"],
    sampleId: ["sampleid", "samplekey", "keyid1"],
    requestDate: ["requestdate", "requestdt", "requesteddate"],
    description: ["description", "sampledescription", "productdescription", "materialdescription"],
  };
  const entries = Object.entries(result);
  Object.entries(aliases).forEach(([key, names]) => {
    if (!samMissing(result[key]) || (Array.isArray(result._manualClears) && result._manualClears.includes(key))) return;
    for (const name of names) {
      const match = entries.find(([candidate, item]) => samNormalizeKey(candidate) === name && !samMissing(item));
      if (match) { result[key] = samClone(match[1]); break; }
    }
  });
  return result;
};
const normalizeSamSource = (value) => {
  const result = samObject(value);
  result.metaData = canonicalSamMetadata(result.metaData);
  const alternate = canonicalSamMetadata(result.metadata);
  Object.entries(alternate).forEach(([key, value]) => {
    if (samMissing(result.metaData[key]) && !samMetadataWasCleared(result.metaData, key)) result.metaData[key] = samClone(value);
  });
  // Some saved sources keep dates/metadata at the source root. Mirror known
  // keys for display without removing their original representation.
  const rootMetadata = canonicalSamMetadata(result);
  [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((key) => {
    if (samMissing(result.metaData[key]) && !samMetadataWasCleared(result.metaData, key) && !samMissing(rootMetadata[key])) result.metaData[key] = samClone(rootMetadata[key]);
  });
  return result;
};
const isManualSamEntry = (key, field = {}) => {
  if (!isManualSamField(key, field)) return false;
  const source = normalizeSamSource(field.dataSource);
  const sourceName = String(source.metaData?.source || "").toLowerCase();
  const sourceType = String(source.type || "").toLowerCase();
  if (sourceName.includes("labvantage") || sourceName.includes("calculator") || sourceName === "api" ||
      sourceType.includes("labvantage") || sourceType.includes("calculator") || sourceType === "api") return false;
  return sourceType === "physical" || sourceType === "virtual" || sourceType === "manual" || sourceName.includes("manual") ||
    (sourceName === "" && field.edited === true);
};
// Fiber values and contact-angle values can always be entered manually; editing switches the field to a manual source.
const isManualFiberEntry = (module, key, field = {}) => module === "fiber" ||
  [key, field.propertyMetaData?.dbColumnName, field.propertyMetaData?.fieldLabel].some((name) => samNormalizeKey(name).includes("contactangle"));
const samMetadataFromRow = (row) => {
  const value = samObject(row);
  const embedded = normalizeSamSource(value.dataSource).metaData;
  const metadata = canonicalSamMetadata(value.metaData ?? value.metadata);
  const direct = canonicalSamMetadata(value);
  const result = { ...embedded, ...metadata };
  [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((key) => {
    if (!samMissing(direct[key])) result[key] = samClone(direct[key]);
  });
  return result;
};
// Some calculator versions put provenance on the output row itself.
const samSourceFromOutput = (row) => {
  const source = normalizeSamSource(row?.dataSource);
  Object.entries(samMetadataFromRow(row)).forEach(([name, value]) => {
    if (samMissing(source.metaData[name]) && !samMetadataWasCleared(source.metaData, name) && !samMissing(value))
      source.metaData[name] = samClone(value);
  });
  return source;
};
const samMetadataWasCleared = (metadata, key) => Array.isArray(metadata?._manualClears) && metadata._manualClears.includes(key);
const trackSamMetadataClear = (metadata, key, value) => {
  if (!samMetadataKeys.includes(key)) return metadata;
  const clears = new Set(Array.isArray(metadata._manualClears) ? metadata._manualClears : []);
  if (value === "" || value === null) clears.add(key); else clears.delete(key);
  if (clears.size) metadata._manualClears = [...clears]; else delete metadata._manualClears;
  return metadata;
};
const commonSamMetadata = (sources) => {
  const metadata = (sources || []).map((source) => normalizeSamSource(source).metaData);
  const result = {};
  if (!metadata.length) return result;
  samMetadataKeys.forEach((key) => {
    // Constants and unannotated inputs must not hide actual selected-record
    // metadata. Keep conflicting values separate in inputMetaData.
    const available = metadata.filter((item) => !samMissing(item[key]));
    const first = available[0]?.[key];
    if (available.length && available.every((item) => JSON.stringify(item[key]) === JSON.stringify(first))) result[key] = samClone(first);
  });
  return result;
};
const buildCalculatedSamSource = (field, inputs, config = {}, propertyKey) => {
  const source = normalizeSamSource(field.dataSource);
  const inputSources = Object.fromEntries(Object.entries(inputs || {}).map(([key, input]) => [key, normalizeSamSource(input?.dataSource)]));
  const method = config.testMethod ?? field.propertyMetaData?.testMethod;
  if (!samMissing(method) && !samMetadataWasCleared(source.metaData, "testMethod") && samMissing(source.metaData.testMethod)) source.metaData.testMethod = samClone(method);
  const common = commonSamMetadata(Object.values(inputSources));
  Object.entries(common).forEach(([key, value]) => {
    if (key !== "source" && !samMetadataWasCleared(source.metaData, key) && samMissing(source.metaData[key])) source.metaData[key] = samClone(value);
  });
  // Hooke's Law can also be a configured local dependent of GelProp_A
  // outputs. Carry that actual request's provenance along the dependency,
  // not from an unrelated sibling, and never substitute a numeric output.
  if (isSamHookesLawField(propertyKey, field)) {
    const named = Object.values(inputSources).map((input) => input.metaData)
      .filter((metadata) => !samMissing(metadata.calculatorName));
    const ownName = samNormalizeKey(source.metaData.calculatorName);
    if (named.length && named.every((metadata) => samNormalizeKey(metadata.calculatorName) === "gelpropa") &&
        (!ownName || ownName === "gelpropa")) {
      samCalculatorMetadataKeys.forEach((name) => {
        const available = named.filter((metadata) => !samMissing(metadata[name]));
        if (samMissing(source.metaData[name]) && available.length &&
            available.every((metadata) => JSON.stringify(metadata[name]) === JSON.stringify(available[0][name])))
          source.metaData[name] = samClone(available[0][name]);
      });
    }
  }
  source.metaData.source = "Calculator";
  source.metaData.inputMetaData = {
    ...samObject(source.metaData.inputMetaData),
    ...Object.fromEntries(Object.entries(inputSources).map(([key, input]) => [key, samClone(input.metaData)])),
  };
  return source;
};
const hydrateSamRecord = (dataSet, fields = {}) => {
  if (!dataSet?.sam?.propertyData) return dataSet;
  const next = samClone(dataSet);
  if (typeof next.sam.propertyData === "string") {
    try { const rows = JSON.parse(next.sam.propertyData); if (Array.isArray(rows)) next.sam.propertyData = rows; } catch { /* Preserve unknown legacy payloads; the view handles missing rows. */ }
  }
  if (!Array.isArray(next.sam.propertyData)) return next;
  const visit = (rows) => (Array.isArray(rows) ? rows : []).forEach((row) => Object.entries(row || {}).forEach(([key, field]) => {
    if (!field || typeof field !== "object" || isManualSamField(key)) return;
    field.dataSource = normalizeSamSource(field.dataSource);
    const rowMetadata = samMetadataFromRow(field);
    [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((name) => {
      if (!samMetadataWasCleared(field.dataSource.metaData, name) && samMissing(field.dataSource.metaData[name]) && !samMissing(rowMetadata[name]))
        field.dataSource.metaData[name] = samClone(rowMetadata[name]);
    });
    if (field.valueTypeArray && typeof field.value === "string") {
      try { const children = JSON.parse(field.value); if (Array.isArray(children)) field.value = children; } catch { /* Keep an unrecognized legacy value intact. */ }
    }
    if (field.valueTypeArray && Array.isArray(field.value)) {
      visit(field.value);
      const children = field.value.flatMap((item) => Object.values(item || {})).filter((item) => item && typeof item === "object");
      // Older records can store calculator provenance on the group OR its
      // children. Restore it in both directions without changing any values.
      if (samNormalizeKey(key) === "loadcoefficients") {
        samCalculatorMetadataKeys.forEach((name) => {
          const available = children.map((item) => normalizeSamSource(item.dataSource).metaData)
            .filter((meta) => !samMissing(meta[name]) &&
              (samMissing(field.dataSource.metaData.calculatorName) || samMissing(meta.calculatorName) || meta.calculatorName === field.dataSource.metaData.calculatorName));
          if (samMissing(field.dataSource.metaData[name]) && available.length &&
              available.every((meta) => JSON.stringify(meta[name]) === JSON.stringify(available[0][name]))) {
            field.dataSource.metaData[name] = samClone(available[0][name]);
          }
        });
        field.value.forEach((row) => Object.entries(row || {}).forEach(([childKey, child]) => {
          if (!child || !(isSamCalculatorDetailField(childKey) || /^[ab]$/.test(samNormalizeKey(childKey)))) return;
          child.dataSource = normalizeSamSource(child.dataSource);
          const childName = child.dataSource.metaData.calculatorName;
          const parentName = field.dataSource.metaData.calculatorName;
          if (!samMissing(childName) && !samMissing(parentName) && childName !== parentName) return;
          [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((name) => {
            if (!samMetadataWasCleared(child.dataSource.metaData, name) && samMissing(child.dataSource.metaData[name]) && !samMissing(field.dataSource.metaData[name]))
              child.dataSource.metaData[name] = samClone(field.dataSource.metaData[name]);
          });
          if (samMissing(child.dataSource.type) && !samMissing(field.dataSource.type)) child.dataSource.type = field.dataSource.type;
        }));
      }
      const common = commonSamMetadata(children.map((item) => item.dataSource));
      Object.entries(common).forEach(([name, value]) => {
        if (!samMetadataWasCleared(field.dataSource.metaData, name) && samMissing(field.dataSource.metaData[name])) field.dataSource.metaData[name] = samClone(value);
      });
    }
  }));
  visit(next.sam.propertyData);
  // Backfill provenance for already-loaded calculated values without changing
  // those values or either manual-entry property. Resolve dependencies first.
  const find = (rows, key) => {
    for (const row of Array.isArray(rows) ? rows : []) {
      if (Object.prototype.hasOwnProperty.call(row || {}, key)) return row[key];
      for (const field of Object.values(row || {})) {
        if (field?.valueTypeArray) { const child = find(field.value, key); if (child) return child; }
      }
    }
  };
  const visited = new Set();
  const fill = (key, path = new Set()) => {
    if (visited.has(key) || path.has(key) || isManualSamField(key)) return;
    const config = fields?.calc?.[key];
    if (!config || config.module !== "sam") return;
    const target = find(next.sam.propertyData, key);
    if (!target || samMissing(target.value)) return;
    // A measured/imported result is not a local calculation merely because
    // a fallback calculator also exists for that property.
    const sourceName = String(target.dataSource?.metaData?.source || "").toLowerCase();
    if (!["", "calculator", "manual"].includes(sourceName) || (target.edited && sourceName === "manual")) return;
    const inputs = {};
    const nextPath = new Set([...path, key]);
    (config.targetCalculationColumn || []).forEach((inputKey) => {
      fill(inputKey, nextPath);
      const inputModule = fields?.fields?.[inputKey]?.module;
      const storage = ["absorbent", "mechanical"].includes(inputModule) ? "material" : inputModule;
      const input = find(next[storage]?.propertyData, inputKey);
      if (input) inputs[inputKey] = input;
    });
    if (Object.keys(inputs).length) target.dataSource = buildCalculatedSamSource(target, inputs, config, key);
    visited.add(key);
  };
  Object.keys(fields?.calc || {}).forEach((key) => fill(key));
  // Calculated child fields may now carry metadata missing from their group.
  visit(next.sam.propertyData);
  return next;
};
// End SAM metadata provenance.

// Local metadata handling: no additional helper file is required.
// Protect SAM provenance in Property Details, including coefficient children.
// This is an editing policy only: imports, hydration and saves keep all metadata.
const canEditPropertyDetail = (module, propertyKey, metadataPath = [], field = {}) => {
  const rootKey = samNormalizeKey(metadataPath[0]);
  const source = module === "sam" ? normalizeSamSource(field.dataSource) : null;
  const sourceName = String(source?.metaData?.source || "").toLowerCase();
  const sourceType = String(source?.type || "").toLowerCase();
  if (module === "sam" && (sourceName.includes("labvantage") || sourceName.includes("calculator") || sourceName === "api" ||
      sourceType.includes("labvantage") || sourceType.includes("calculator") || sourceType === "api")) return false;
  // Manual SAM metadata is editable; API and calculator provenance is read-only.
  if (module === "sam" && isManualSamEntry(propertyKey, field) && metadataPath.length === 1 &&
      ["source", "datasource", "description"].includes(rootKey)) return true;
  if (module === "fiber" && ["source", "datasource", "description"].includes(rootKey)) return true;
  // Material properties follow the Fiber rule: Data Source/Description are editable in edit mode.
  if (["material", "absorbent", "mechanical"].includes(module) && ["source", "datasource", "description"].includes(rootKey)) return true;
  if (["source", "datasource", "description", "type", "propertyname"].includes(rootKey)) return false;
  return module !== "sam" || !samCalculatorMetadataKeys.some((key) => samNormalizeKey(key) === rootKey);
};

const clonePropertyData = (value) => {
  if (Array.isArray(value)) return value.map(clonePropertyData);
  if (value instanceof Date) return new Date(value.getTime());
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clonePropertyData(item)]));
  }
  return value;
};

const findProperty = (rows, key) => {
  const items = Array.isArray(rows) ? rows : [];
  for (const row of items) {
    if (row && Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  }
  for (const row of items) {
    for (const field of Object.values(row || {})) {
      if (field?.valueTypeArray) {
        const child = findProperty(field.value, key);
        if (child) return child;
      }
    }
  }
  return undefined;
};

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const isMissing = (value) => value === undefined || value === null || value === "" || value === "-";

const forbiddenKeys = new Set(["__proto__", "constructor", "prototype"]);

const ensureDataSource = (current, defaults = {}, propertyName) => {
  current = normalizeSamSource(current);
  const result = clonePropertyData(isObject(defaults) ? defaults : {});
  Object.entries(isObject(current) ? current : {}).forEach(([key, value]) => {
    if (forbiddenKeys.has(key) || value === undefined) return;
    if (key !== "metaData") result[key] = clonePropertyData(value);
  });
  result.metaData = {
    ...(isObject(defaults?.metaData) ? clonePropertyData(defaults.metaData) : {}),
    ...(isObject(current?.metaData) ? clonePropertyData(current.metaData) : {}),
  };
  if (propertyName) result.propertyName = propertyName;
  return result;
};

const setMetadataValue = (source, path, value) => {
  const result = ensureDataSource(source);
  const keys = Array.isArray(path) ? path : [path];
  if (!keys.length || keys.some((key) => forbiddenKeys.has(key))) return result;
  let cursor = result.metaData;
  keys.slice(0, -1).forEach((key) => {
    if (!isObject(cursor[key])) cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[keys[keys.length - 1]] = value;
  if (keys.length === 1) trackSamMetadataClear(result.metaData, keys[0], value);
  return result;
};

const markManualSource = (field, defaults, propertyName, author) => {
  field.dataSource = ensureDataSource(field.dataSource, defaults, propertyName);
  field.dataSource.type = field.virtual ? "virtual" : "physical";
  // Preserve descriptions, dates, sample IDs and field-specific metadata, but
  // do not mislabel a manually changed value as an untouched API result.
  if (defaults?.metaData?.source !== undefined) field.dataSource.metaData.source = defaults.metaData.source;
  if (author) field.dataSource.metaData.author = author;
};

const equal = (left, right) => {
  if (Object.is(left, right)) return true;
  if (left instanceof Date || right instanceof Date) return String(left) === String(right);
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((v, i) => equal(v, right[i]));
  }
  if (!isObject(left) || !isObject(right)) return false;
  const leftKeys = Object.keys(left).filter((key) => left[key] !== undefined);
  const rightKeys = Object.keys(right).filter((key) => right[key] !== undefined);
  return leftKeys.length === rightKeys.length && leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && equal(left[key], right[key]));
};

const propertyValuesEqual = (left, right, inputType) => {
  if (isMissing(left) || isMissing(right)) return isMissing(left) && isMissing(right);
  if (inputType === "number") {
    const l = Number(left), r = Number(right);
    return Number.isFinite(l) && Number.isFinite(r) && l === r;
  }
  return equal(left, right);
};

const propertyMap = (rows) => Object.fromEntries(
  (Array.isArray(rows) ? rows : []).flatMap((row) => Object.entries(row || {}))
);

const fieldHasChanges = (current, original) => {
  if (!current || !original) return current !== original;
  const nested = current.valueTypeArray || original.valueTypeArray;
  const sameValue = nested
    ? !propertyRowsHaveChanges(current.value, original.value)
    : propertyValuesEqual(current.value, original.value, current.propertyMetaData?.fieldType || original.propertyMetaData?.fieldType);
  return !sameValue || !!current.virtual !== !!original.virtual || !equal(current.dataSource, original.dataSource);
};

const propertyRowsHaveChanges = (currentRows, originalRows) => {
  const current = propertyMap(currentRows), original = propertyMap(originalRows);
  const keys = new Set([...Object.keys(current), ...Object.keys(original)]);
  return [...keys].some((key) => fieldHasChanges(current[key], original[key]));
};

const storageModule = (module) => ["mechanical", "absorbent"].includes(module) ? "material" : module;

const runAutoCalculationCheck = (dataSet, sourceKey, fields) => {
  (fields?.fields?.[sourceKey]?.targetCalculatorField || []).forEach((targetKey) => {
    const config = fields?.calc?.[targetKey];
    if (!config?.targetCalculationColumn?.length || !config.formulaUsed) return;
    const targetModule = storageModule(config.module);
    const target = findProperty(dataSet?.[targetModule]?.propertyData, targetKey);
    const normalizedTargetKey = samNormalizeKey(targetKey);
    if (!target ||
        (targetModule === "sam" && isManualSamField(targetKey)) ||
        (targetModule === "material" && normalizedTargetKey === "solidscompositedensity" && target.edited)) return;
    const inputValues = {};
    const inputFields = {};
    for (const inputKey of config.targetCalculationColumn) {
      const module = storageModule(fields?.fields?.[inputKey]?.module);
      const input = findProperty(dataSet?.[module]?.propertyData, inputKey);
      if (!input) return;
      inputValues[inputKey] = input.value;
      inputFields[inputKey] = input;
      if (module === "sam" && ["solidsDryGelRadius", "solidsDryGelDensity", "contactAngle"].includes(inputKey) &&
          ["", null, 0, "-"].includes(inputValues[inputKey])) inputValues[inputKey] = 1;
    }
    target.value = runAutoCalculation(inputValues, config.formulaUsed, config.index);
    if (storageModule(config.module) === "sam") target.dataSource = buildCalculatedSamSource(target, inputFields, config, targetKey);
  });
  return dataSet;
};

// Decode recognized legacy row arrays without deleting unrecognized payloads.
const hydrateDetailsRecord = (record, fields) => {
  let next = hydrateSamRecord(record, fields);
  for (const module of ["material", "fiber"]) {
    if (typeof next?.[module]?.propertyData !== "string") continue;
    try {
      const rows = JSON.parse(next[module].propertyData);
      if (Array.isArray(rows)) {
        next = { ...next, [module]: { ...next[module], propertyData: rows } };
      }
    } catch { /* Preserve unknown legacy data; do not erase it. */ }
  }
  return next;
};

function InputGrid({
  moduleFieldDataSet,
  moduleDefaultFieldDataSet,
  moduleName,
  editStatus,
  handleLabVantageApiCallPopup,
  handleFieldDataUpdate,
  handleHeaderDataUpdate,
  fields,
}) {
  const location = useLocation();
  const defaultMetaDataFieldsList = useSelector((state) => state.defaultMetaDataFields);
  const userInfo = useSelector((state) => state.userInfo);
  const [dataSet, setDataSet] = useState(moduleDefaultFieldDataSet);
  const draftRef = useRef(moduleDefaultFieldDataSet);
  const [activeRow, setActiveRow] = useState({});
  const defaultTabKey =
    moduleName === "sam"
      ? "3"
      : moduleName === "fiber"
      ? "4"
      : "0";
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey);
  const pageIdentity = `${moduleName || ""}|${location?.pathname || ""}|${location?.key || ""}`;
  const tabPageRef = useRef(pageIdentity);
  useEffect(() => {
    tabPageRef.current = pageIdentity;
    setActiveTabKey(defaultTabKey);
  }, [pageIdentity, defaultTabKey]);
  const [focusStates, setFocusStates] = useState({});
  const [hoverState, setHoverState] = useState({ load: false, swelling: false });
  const [loadCoefficientsTooltipText, setLoadCoefficientstooltipText] = useState("");
  const [swellingCoefficientsTooltipText, setSwellingCoefficientsTooltipText] = useState("");

  const handleMouseEnterLoad = () => setHoverState((prev) => ({ ...prev, load: true }));
  const handleMouseLeaveLoad = () => setHoverState((prev) => ({ ...prev, load: false }));
  const handleMouseEnterSwelling = () => setHoverState((prev) => ({ ...prev, swelling: true }));
  const handleMouseLeaveSwelling = () => setHoverState((prev) => ({ ...prev, swelling: false }));

  useEffect(() => {
    const hydrated = hydrateDetailsRecord(moduleDefaultFieldDataSet, fields);
    draftRef.current = hydrated;
    setDataSet(hydrated);
  }, [moduleDefaultFieldDataSet, editStatus, fields]);

  useEffect(() => {
    if (!location?.state || Object.keys(dataSet || {}).length === 0) {
      return;
    }

    const moduleIdByTabLabel = {
      "Main Material Properties": "material",
      "Absorbent Properties": "absorbent",
      "Mechanical Properties": "mechanical",
      "SAM Properties": "sam",
      "Fiber Properties": "fiber",
    };

    const normalizeField = (value) => String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const stateTabLabel = location.state.activeTab;
    const stateTargetField = location.state.targetFieldKey;

    // Navigation state may identify a property, but never overrides the
    // requested first-tab default. Manual tab changes remain controlled.

    if (!stateTargetField || !stateTabLabel || !moduleIdByTabLabel[stateTabLabel]) {
      return;
    }

    const targetModule = moduleIdByTabLabel[stateTabLabel];
    const targetFieldNormalized = normalizeField(stateTargetField);

    let moduleRows = [];

    if (targetModule === "material") {
      moduleRows = (Array.isArray(dataSet?.material?.propertyData) ? dataSet.material.propertyData : []).filter(
        (item) =>
          item?.[Object.keys(item || {})[0]]?.propertyMetaData?.tab === "material" ||
          Object.keys(item || {}).includes("materialSpec")
      );
    } else if (targetModule === "absorbent" || targetModule === "mechanical") {
      moduleRows = (Array.isArray(dataSet?.material?.propertyData) ? dataSet.material.propertyData : []).filter(
        (item) => item?.[Object.keys(item || {})[0]]?.propertyMetaData?.tab === targetModule
      );
    } else {
      moduleRows = Array.isArray(dataSet?.[targetModule]?.propertyData) ? dataSet[targetModule].propertyData : [];
    }

    const targetIndex = moduleRows.findIndex((row) => {
      const rowKey = Object.keys(row || {})[0];
      const rowMetaData = rowKey ? row[rowKey]?.propertyMetaData : null;

      return (
        normalizeField(rowKey) === targetFieldNormalized ||
        normalizeField(rowMetaData?.dbColumnName) === targetFieldNormalized ||
        normalizeField(rowMetaData?.fieldLabel) === targetFieldNormalized
      );
    });

    if (targetIndex > -1) {
      const targetRowKey = Object.keys(moduleRows[targetIndex])[0];
      setActiveRow((previous) => ({
        ...(previous || {}),
        [targetModule]: {
          moduleType: targetModule,
          key: targetRowKey,
          index: targetIndex,
        },
      }));
    }
  }, [location?.state, dataSet]);

  useEffect(() => {
    setLoadCoefficientstooltipText(tooltipText("lc"));
    setSwellingCoefficientsTooltipText(tooltipText("sc"));
  }, [dataSet?.sam?.propertyData]);

  // Initialize selections outside render. Functional updates preserve other tabs'
  // selections instead of replacing them with a stale activeRow snapshot.
  useEffect(() => {
    setActiveRow((previous) => {
      const next = { ...(previous || {}) };
      let changed = false;
      ["material", "absorbent", "mechanical", "sam", "fiber"].forEach((module) => {
        const storageModule = module === "absorbent" || module === "mechanical" ? "material" : module;
        let rows = Array.isArray(dataSet?.[storageModule]?.propertyData) ? dataSet[storageModule].propertyData : [];
        if (["material", "absorbent", "mechanical"].includes(module)) {
          rows = rows.filter((row) => {
            const key = Object.keys(row || {})[0];
            return row?.[key]?.propertyMetaData?.tab === module ||
              (module === "material" && key === "materialSpec");
          });
        }
        if (next[module]?.key && rows.some((row) =>
          Object.prototype.hasOwnProperty.call(row || {}, next[module].key)
        )) return;
        const firstKey = Object.keys(rows[0] || {})[0];
        if (firstKey) {
          next[module] = { moduleType: module, key: firstKey, index: 0 };
          changed = true;
        } else if (next[module]) {
          delete next[module];
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [dataSet]);

  const samDisplayLabelMap = {
    samCRC: "SAM CRC",
    vortexTime: "Vortex Time",
    gelBedPermeability: "Gel Bed Permeability",
    freeswellCapacity: "Freeswell Capacity",
    maxCapacity: "SAM Capacity @0.3 psi",
    "1secondAUL": "1sec AUL",
    "0.3psiGBP": "0.3 psi GBP",
    moisture: "Moisture",
  };

  const getDisplayFieldLabel = (metaData) => {
    if (!metaData) return "";
    return samDisplayLabelMap[metaData.dbColumnName] || metaData.fieldLabel;
  };

  const orderSamPropertyRows = (propertyRows = []) => {
    const rows = Array.isArray(propertyRows) ? [...propertyRows] : [];
    const normalizeField = (value) => String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const getRowMetaData = (row) => {
      const rowKey = Object.keys(row || {})[0];
      return rowKey ? row[rowKey]?.propertyMetaData || {} : {};
    };
    const rowMatches = (row, candidates) => {
      const rowKey = Object.keys(row || {})[0];
      const metaData = getRowMetaData(row);
      const values = [rowKey, metaData.dbColumnName, metaData.fieldLabel].map(normalizeField);
      return candidates.some((candidate) => {
        const normalizedCandidate = normalizeField(candidate);
        return values.some((value) => value && (
          value === normalizedCandidate ||
          value.includes(normalizedCandidate) ||
          normalizedCandidate.includes(value)
        ));
      });
    };
    const isGelDiffusivityRow = (row) => rowMatches(row, ["gelDiffusivity", "Gel Diffusivity"]);
    if (rows.findIndex(isGelDiffusivityRow) < 0) return rows;
    const priorityRows = [];
    [
      ["swellingCoefficients", "Swelling Coefficients"],
      ["maxCapacity", "SAM Capacity @0.3 psi", "Max Capacity @0.3 psi"],
    ].forEach((priorityCandidates) => {
      const currentIndex = rows.findIndex((row) => rowMatches(row, priorityCandidates));
      if (currentIndex > -1) priorityRows.push(rows.splice(currentIndex, 1)[0]);
    });
    const gelDiffusivityIndex = rows.findIndex(isGelDiffusivityRow);
    rows.splice(gelDiffusivityIndex, 0, ...priorityRows);
    return rows;
  };

  function tooltipText(type) {
    const { a1, b1, a2, maxCapacity } = tooltipData();
    if (type === "lc") return `AUL = Exp(${a1} + ${b1} * Load)`;
    if (type === "sc") return `AUL = ${maxCapacity} - (4 x ${maxCapacity} )/(2+ ${a2} * time * sqrt( ${maxCapacity} ))^2`;
    return "";
  }

  function tooltipData() {
    const tooltipMapper = { a1: "A", a2: "A", b1: "B", maxCapacity: "SAM Capacity @0.3 psi" };
    (Array.isArray(dataSet?.sam?.propertyData) ? dataSet.sam.propertyData : []).forEach((properties) => {
      if (Array.isArray(properties?.loadCoefficients?.value)) {
        properties.loadCoefficients.value.forEach((d) => {
          if (d?.loadCoefficientsA?.value && d.loadCoefficientsA.value !== "-") tooltipMapper.a1 = d.loadCoefficientsA.value;
          if (d?.loadCoefficientsB?.value && d.loadCoefficientsB.value !== "-") tooltipMapper.b1 = d.loadCoefficientsB.value;
        });
      } else if (Array.isArray(properties?.swellingCoefficients?.value)) {
        properties.swellingCoefficients.value.forEach((d) => {
          if (d?.swellingCoefficientsK?.value && d.swellingCoefficientsK.value !== "-") tooltipMapper.a2 = d.swellingCoefficientsK.value;
        });
      } else if (properties?.["1secondAUL"]?.value && properties["1secondAUL"].value !== "-") {
        tooltipMapper.maxCapacity = properties["1secondAUL"].value;
      } else if (properties?.maxCapacity?.value && properties.maxCapacity.value !== "-") {
        tooltipMapper.maxCapacity = properties.maxCapacity.value;
      } else if (properties?.["0.3psiGBP"]?.value && properties["0.3psiGBP"].value !== "-") {
        tooltipMapper.maxCapacity = properties["0.3psiGBP"].value;
      }
    });
    return tooltipMapper;
  }

  const handleLabVantageApiCall = (item, key, module, index, fieldLabel) => {
    const newModule = module === "mechanical" || module === "absorbent" ? "material" : module;
    return handleLabVantageApiCallPopup(true, item, key, newModule, index, fieldLabel);
  };

  const fieldDataUpdate = (data) => {
    // Commit synchronously before a following blur/metadata event reads the draft.
    draftRef.current = data;
    setDataSet(data);
    handleFieldDataUpdate(data);
  };

  const renderGridHeader = (module) => (
    <div className="row headerRow">
      <div className="col-3 col-md-3 col-sm-3"><span>{module}</span> Properties</div>
      <div className="col-5 col-md-5 col-sm-5">Values</div>
      {module === "sam" && <div className="col-1 col-md-1 col-sm-1" />}
      <div className="col-1 col-md-1 col-sm-1 center">Virtual</div>
      {editStatus ? <div className="col-2 col-md-2 col-sm-2 center">Get Data</div> : null}
    </div>
  );

  const checkInputRange = (finalInput, inputData, key) => {
    const normalizedKey = String(key || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const isGelBedField = normalizedKey.includes("gelbedpermeability");
    const isMaterialDensity = normalizedKey === "solidscompositedensity";
    const minRange = isGelBedField || isMaterialDensity ? 0 : inputData[key].propertyMetaData.fieldMinRange;
    const maxRange = isGelBedField ? 5000 : inputData[key].propertyMetaData.fieldMaxRange;
    if (
      finalInput !== "" &&
      finalInput !== "-" &&
      finalInput !== null &&
      finalInput !== undefined &&
      !Number.isNaN(Number(finalInput)) &&
      (Number(finalInput) < Number(minRange) || Number(finalInput) > Number(maxRange))
    ) {
      return `Value is out of range(Min: ${minRange}, Max: ${maxRange})`;
    }
    return "";
  };

  const checkArrayInputRange = (value, propertyMetaData) => {
    if (value === "-" || value === "" || isNaN(Number(value))) return "";
    const numericValue = Number(value);
    const minVal = propertyMetaData.fieldMinRange;
    const maxVal = propertyMetaData.fieldMaxRange;
    if (typeof minVal === "number" && typeof maxVal === "number" && (numericValue < minVal || numericValue > maxVal)) {
      return `Value is out of range(Min: ${minVal}, Max: ${maxVal})`;
    }
    return "";
  };

  const renderArrayTypeInputField = (inputData, key, module, index) => {
    if (!inputData[key]?.valueTypeArray || !Array.isArray(inputData[key].value)) return null;
    return (
      <Input.Group size="large" compact>
        {inputData[key].value.map((item, tempIndex) => {
          for (const tempKey in item) {
            if (!item[tempKey] || typeof item[tempKey] !== "object") continue;
            const propertyMeta = item[tempKey]?.propertyMetaData || {};
            const value = item[tempKey].value;
            const safeInputValue = propertyMeta.fieldType === "number" && (value === "-" || value == null) ? "" : value;
            const rangeAlert = checkArrayInputRange(value, propertyMeta);
            return (
              <div key={`${propertyMeta.dbColumnName}-${tempIndex}`} style={{ width: `${100 / inputData[key].value.length}%`, display: "inline-block", verticalAlign: "top" }}>
                <Input
                  type={propertyMeta.fieldType}
                  readOnly={!editStatus || (propertyMeta.calculatedField === true && !isManualFiberEntry(module, tempKey, item[tempKey]))}
                  value={safeInputValue}
                  style={{ width: "100%" }}
                  addonAfter={propertyMeta.fieldUnit}
                  placeholder="-"
                  aria-label="-"
                  aria-describedby={`basic-${propertyMeta.dbColumnName}`}
                  status={rangeAlert && editStatus ? "error" : ""}
                  onFocus={() => setActiveRow((previous) => ({ ...previous, [module]: { moduleType: module, key, childKey: tempKey, index } }))}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveRow((previous) => ({ ...previous, [module]: { moduleType: module, key, childKey: tempKey, index } }));
                  }}
                  onChange={(e) => updateFieldValue(propertyMeta.fieldType, module, inputData, key, index, e.target.value, tempIndex, tempKey, false)}
                />
                {rangeAlert && editStatus ? <div className="invalid-feedback" style={{ display: "inline-block" }}>{rangeAlert}</div> : null}
              </div>
            );
          }
          return null;
        })}
      </Input.Group>
    );
  };

  const renderFieldValue = (inputData, key, module, index) => {
    const finalInput = inputData[key].valueTypeArray
      ? ""
      : inputData[key].value !== null && inputData[key].value !== "" ? inputData[key].value : "-";
    const rangeAlert = checkInputRange(finalInput, inputData, key);

    const renderInputField = () => {
      if (inputData[key].propertyMetaData.dbColumnName === "materialSpec") {
        return (
          <Input.TextArea
            readOnly={!editStatus}
            value={inputData[key].value}
            placeholder=""
            aria-label="-"
            aria-describedby={`basic-${inputData[key].propertyMetaData.dbColumnName}`}
            onChange={(e) => updateFieldValue(inputData[key].propertyMetaData.fieldType, module, inputData, key, index, e, null, null, false)}
          />
        );
      }
      if (inputData[key].propertyMetaData.fieldType === "number") {
        const isOneSecondAul = inputData[key].propertyMetaData.dbColumnName === "1secondAUL";
        const normalizedFieldKey = String(key || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
        const isGelBedPermeability = normalizedFieldKey.includes("gelbedpermeability");
        const isMoistureField = normalizedFieldKey.includes("moist");
        const isMaterialDensity = normalizedFieldKey === "solidscompositedensity";
        const isEditableMaterialDensity =
          ["material", "absorbent", "mechanical"].includes(module) &&
          normalizedFieldKey.includes("density");

        const handleFocus = (fieldKey) => setFocusStates((previous) => ({ ...previous, [fieldKey]: true }));
        const handleBlur = (fieldKey, value) => {
          setFocusStates((previous) => ({ ...previous, [fieldKey]: false }));
          if (!isOneSecondAul && !isGelBedPermeability && !isMoistureField) return;
          let formatted = "";
          if (value !== "" && value !== null && value !== undefined && !Number.isNaN(Number(value))) {
            const parsed = Number(value);
            if (isOneSecondAul) formatted = Math.min(100, Math.max(0, parsed)).toFixed(2);
            else if (isGelBedPermeability) formatted = Math.min(5000, Math.max(0, parsed)).toFixed(2);
            else formatted = parsed.toFixed(1);
          }
          // Formatting commits the value only. It must not select the blurred row
          // or remove its dataSource when the numeric value did not change.
          updateFieldValue(inputData[fieldKey].propertyMetaData.fieldType, module, inputData, fieldKey, index, formatted, null, null, false);
        };

        const getFormattedValue = (fieldKey, val) => {
          if (val === "" || val === null || val === undefined || val === "-") return "";
          const numVal = Number(val);
          if (focusStates[fieldKey]) return Number.isNaN(numVal) ? "" : val;
          if (Number.isNaN(numVal)) return "";
          if (isOneSecondAul) return Math.min(100, Math.max(0, numVal)).toFixed(2);
          if (isGelBedPermeability) return numVal.toFixed(2);
          if (isMoistureField) return numVal.toFixed(1);
          if (isMaterialDensity) return numVal.toFixed(4);
          if (module === "fiber") return String(val);
          if (numVal === 0) return "0";
          const digitStr = numVal.toString().replace(/[-.]/g, "");
          return digitStr.length <= 4 ? numVal : numVal.toExponential(3);
        };

        const fiberPrecision = module === "fiber"
          ? Math.max(
              Number(inputData[key].propertyMetaData.fieldPrecision) || 0,
              4,
              ...[inputData[key].propertyMetaData.fieldMinRange, inputData[key].propertyMetaData.fieldMaxRange]
                .map((range) => {
                  const text = String(range ?? "");
                  return text.includes(".") ? text.split(".")[1].length : 0;
                })
            )
          : 4;

        return (
          <Input
            key={key}
            size="large"
            type="number"
            readOnly={!editStatus || (!isEditableMaterialDensity && inputData[key].propertyMetaData.calculatedField === true && !isManualSamEntry(key, inputData[key]) && !isManualFiberEntry(module, key, inputData[key]))}
            value={getFormattedValue(key, inputData[key].value)}
            precision={fiberPrecision}
            min={isOneSecondAul || isGelBedPermeability ? 0 : undefined}
            max={isOneSecondAul ? 100 : isGelBedPermeability ? 5000 : undefined}
            step={isOneSecondAul ? 0.01 : isGelBedPermeability || isMoistureField ? 0.1 : inputData[key].propertyMetaData.fieldMinRange || 1}
            placeholder=""
            aria-label="-"
            aria-describedby={`basic-${inputData[key].propertyMetaData.dbColumnName}`}
            addonAfter={inputData[key].propertyMetaData.fieldUnit}
            status={rangeAlert !== "" && editStatus === true ? "error" : null}
            onClick={(event) => event.stopPropagation()}
            onChange={(e) => updateFieldValue(inputData[key].propertyMetaData.fieldType, module, inputData, key, index, e.target.value, null, null, false)}
            onFocus={() => handleFocus(key)}
            onBlur={(e) => handleBlur(key, e.target.value)}
          />
        );
      }
      return (
        <Input
          size="large"
          type={inputData[key].propertyMetaData.fieldType}
          disabled={!editStatus}
          value={inputData[key].value}
          placeholder=""
          aria-label="-"
          aria-describedby={`basic-${inputData[key].propertyMetaData.dbColumnName}`}
          addonAfter={inputData[key].propertyMetaData.fieldUnit}
          onClick={(event) => event.stopPropagation()}
          onChange={(e) => updateFieldValue(inputData[key].propertyMetaData.fieldType, module, inputData, key, index, e, null, null, false)}
        />
      );
    };
    return (
      <div>
        {inputData[key].valueTypeArray ? renderArrayTypeInputField(inputData, key, module, index) : renderInputField()}
        {rangeAlert !== "" && editStatus === true ? <div className="invalid-feedback" style={{ display: "inline" }}>{rangeAlert}</div> : null}
      </div>
    );
  };

  const renderLabelValue = (item) => {
    if (!item.loadCoefficients && !item.swellingCoefficients) return null;
    const renderTooltip = (kind, visible, onEnter, onLeave, text) => (
      <span key={kind}>
        <button
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          onClick={(event) => event.stopPropagation()}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "1.2rem" }}
        ><strong>i</strong></button>
        {visible && <div style={{ position: "absolute", top: "-10px", left: "100%", backgroundColor: "#333", color: "#fff", padding: "5px 8px", borderRadius: "3px", whiteSpace: "nowrap", fontSize: "0.65rem", zIndex: 999 }}>{text}</div>}
      </span>
    );
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        {item.loadCoefficients && renderTooltip("load", hoverState.load, handleMouseEnterLoad, handleMouseLeaveLoad, loadCoefficientsTooltipText)}
        {item.swellingCoefficients && renderTooltip("swelling", hoverState.swelling, handleMouseEnterSwelling, handleMouseLeaveSwelling, swellingCoefficientsTooltipText)}
      </div>
    );
  };

  const updateMetaData = (e, metaDatakey, module, parentPath = []) => {
    const key = activeRow?.[module]?.key;
    const selectedKey = activeRow?.[module]?.childKey || key;
    if (!key || !editStatus) return;
    const next = cloneDeep(draftRef.current || {});
    const storageModule = ["mechanical", "absorbent"].includes(module) ? "material" : module;
    const parent = findProperty(next[storageModule]?.propertyData, key);
    const childKey = activeRow?.[module]?.childKey;
    const field = childKey ? findProperty(parent?.value, childKey) : parent;
    if (!field || !canEditPropertyDetail(module, selectedKey, [...parentPath, metaDatakey], field)) return;
    field.dataSource = ensureDataSource(field.dataSource, defaultMetaDataFieldsList?.data?.default, childKey || key);
    field.dataSource = setMetadataValue(field.dataSource, [...parentPath, metaDatakey], e.target.value);
    const originalParent = findProperty(moduleFieldDataSet?.[storageModule]?.propertyData, key);
    const original = childKey ? findProperty(originalParent?.value, childKey) : originalParent;
    field.edited = fieldHasChanges(field, original);
    if (childKey) parent.edited = fieldHasChanges(parent, originalParent);
    fieldDataUpdate(hydrateSamRecord(next, fields));
  };

  const updateFieldValue = (inputType, module, inputData, key, index, e, tempIndex, tempKey, isVirtual) => {
    let next = cloneDeep(draftRef.current || {});
    const storageModule = ["mechanical", "absorbent"].includes(module) ? "material" : module;
    let field = findProperty(next[storageModule]?.propertyData, key);
    if (!field) return;
    const original = findProperty(moduleFieldDataSet?.[storageModule]?.propertyData, key);
    const defaults = defaultMetaDataFieldsList?.data?.default || {};
    const author = userInfo?.sAMAccountName;
    if (isVirtual) {
      const checked = !!e?.target?.checked;
      if (!!field.virtual === checked) return;
      field.virtual = checked;
      markManualSource(field, defaults, key, author);
      if (field.valueTypeArray && Array.isArray(field.value)) {
        field.value.forEach((row) => Object.entries(row || {}).forEach(([childKey, child]) => {
          child.virtual = checked;
          markManualSource(child, defaults, childKey, author);
          child.edited = fieldHasChanges(child, findProperty(original?.value, childKey));
        }));
      }
    } else {
      const child = field.valueTypeArray ? field.value?.[tempIndex]?.[tempKey] : field;
      if (!child) return;
      const raw = e && typeof e === "object" && e.target ? e.target.value : e;
      let value = raw;
      if (inputType === "number") {
        if (raw === null || raw === undefined || raw === "" || raw === "-") value = "";
        else if (String(key).replace(/[^a-zA-Z0-9]/g, "").toLowerCase().includes("gelbedpermeability")) value = raw;
        else value = Number.isFinite(Number(raw)) ? Number(raw) : "";
      }
      // Formatting-only blur must not mutate metadata or steal row focus.
      if (propertyValuesEqual(child.value, value, inputType)) {
        if (!field.dataSource) {
          field.dataSource = ensureDataSource(original?.dataSource, defaults, key);
          fieldDataUpdate(next);
        }
        return;
      }
      child.value = value;
      markManualSource(child, defaults, field.valueTypeArray ? tempKey : key, author);
      if (field.valueTypeArray) {
        child.edited = fieldHasChanges(child, findProperty(original?.value, tempKey));
        markManualSource(field, defaults, key, author);
      }
      const calculationKey = field.valueTypeArray ? tempKey : key;
      const config = fields?.fields?.[calculationKey];
      if (config?.targetCalculatorField?.length) {
        next = runAutoCalculationCheck(next, calculationKey, fields);
        field = findProperty(next[storageModule]?.propertyData, key);
      }
    }
    // Never restore oldDataSource when a value reverts: metadata edits are
    // independent of value edits and must remain in the current draft.
    field.edited = fieldHasChanges(field, original);
    fieldDataUpdate(hydrateSamRecord(next, fields));
  };

  const renderVirtualCheckBox = (item, key, module, index) => {
    if (!item[key].propertyMetaData.fieldVirtualSupport) return null;
    return (
      <Checkbox
        id={`virtual-${key}-${index}`}
        disabled={!editStatus}
        checked={item[key].virtual}
        onChange={(e) => updateFieldValue("checkbox", module, item, key, index, e, null, null, true)}
      />
    );
  };

  const renderAPILink = (item, key, module, index, fieldLabel) => {
    if (!editStatus) return null;
    return (
      <div
        className="col-2 col-md-2 col-sm-2 center hover ttl"
        style={{ marginTop: "auto", marginBottom: "auto" }}
        onClick={(event) => {
          event.stopPropagation();
          handleLabVantageApiCall(item, key, module, index, fieldLabel);
        }}
      ><i className="fas fa-angle-double-right" /> API</div>
    );
  };

  const handleClickedRow = (module, metaData, index, key) => {
    setActiveRow((previous) => {
      if (previous?.[module]?.key === key && previous[module].index === index && !previous[module].childKey) return previous;
      return { ...(previous || {}), [module]: { moduleType: module, key, index } };
    });
  };

  const renderProperties = (data, module) => {
    const validRows = (Array.isArray(data?.[module]?.propertyData) ? data[module].propertyData : [])
      .filter((row) => row && typeof row === "object" && Object.values(row).some((field) => field?.propertyMetaData));
    const propertyRows = module === "sam" ? orderSamPropertyRows(validRows) : validRows;
    return propertyRows.map((item, index) => {
      for (const key in item) {
        const metaData = item[key]?.propertyMetaData;
        if (!metaData) continue;
        const activeClass = activeRow?.[module]?.key === key ? "row colspacing bg-light-row active23" : "row colspacing bg-light-row";
        const selectRow = () => handleClickedRow(module, metaData, index, key);
        return (
          <div
            key={`${module}-${key}-${index}`}
            className={activeClass}
            onPointerDownCapture={selectRow}
            onFocusCapture={selectRow}
            onClickCapture={selectRow}
          >
            <div className="col-3 col-md-3 col-sm-3 cursor" style={{ marginTop: "auto", marginBottom: "auto" }}>{getDisplayFieldLabel(metaData)}</div>
            <div className="col-5 col-md-5 col-sm-5" onClick={(event) => event.stopPropagation()}>{renderFieldValue(item, key, module, index)}</div>
            {module === "sam" && <div className="col-1 col-md-1 col-sm-1 center" style={{ marginTop: "auto", marginBottom: "auto" }}>{renderLabelValue(item)}</div>}
            <div className="col-1 col-md-1 col-sm-1 center" style={{ marginTop: "auto", marginBottom: "auto" }}>{renderVirtualCheckBox(item, key, module, index)}</div>
            {(metaData.calculationForm || metaData.labVantageApi || metaData.compression) && renderAPILink(item, key, module, index, getDisplayFieldLabel(metaData))}
            <i className="fas fa-caret-right fff" />
          </div>
        );
      }
      return null;
    });
  };

  const renderMetadataFields = (items, edited, module, parentPath = [], selectedField = {}) => {
    const config = defaultMetaDataFieldsList?.data?.fields || {};
    const values = items && typeof items === "object" ? items : {};
    const labels = { source: "Data Source", description: "Description", calculatorName: "calculatorName", inputParameters: "inputParameters", calculatorVersion: "calculatorVersion" };
    // Property Details is a display allowlist, not a save-payload filter.
    // Keep internal source/type/propertyName and other metadata in the draft.
    const selectedPropertyKey = activeRow?.[module]?.childKey || activeRow?.[module]?.key;
    const calculatorDetails = module === "sam" && (usesSamCalculatorDetails(selectedPropertyKey, values) ||
      (samNormalizeKey(activeRow?.[module]?.key) === "loadcoefficients" && !!activeRow?.[module]?.childKey)) &&
      (String(values.source || "").toLowerCase() === "calculator" || !samMissing(values.calculatorName) || !samMissing(values.inputParameters));
    const keys = parentPath.length ? Object.keys(values) : calculatorDetails ? [
      "source", "author", "testMethod", "creationDate", "repCount", "sampleId", "requestDate", "description",
      "calculatorName", "inputParameters", "calculatorVersion",
    ] : [
      "source", "author", "testMethod", "creationDate",
      "repCount", "sampleId", "requestDate", "description",
    ];
    return keys.map((key) => {
      const currentField = config[key];
      if (parentPath.length && currentField?.render === false) return null;
      const value = values[key];
      if (value !== null && typeof value === "object") {
        return <tr key={`meta-object-${key}`}><td>{currentField?.label || labels[key] || key}</td><td><table className="table table-bordered table-sm labVantageFieldsTable"><tbody>{renderMetadataFields(value, edited, module, [...parentPath, key], selectedField)}</tbody></table></td></tr>;
      }
      const selectedKey = activeRow?.[module]?.childKey || activeRow?.[module]?.key;
      const requiredManualMetadata = module === "sam" && isManualSamEntry(selectedKey, selectedField) &&
        parentPath.length === 0 && ["source", "description"].includes(samNormalizeKey(key));
      const fiberMetadataEditable = module === "fiber" && parentPath.length === 0 &&
        ["source", "description"].includes(samNormalizeKey(key));
      const materialMetadataEditable = ["material", "absorbent", "mechanical"].includes(module) && parentPath.length === 0 &&
        ["source", "description"].includes(samNormalizeKey(key));
      const editable = editStatus === true && (fiberMetadataEditable || materialMetadataEditable || edited &&
        (requiredManualMetadata || currentField?.editable === true)) &&
        canEditPropertyDetail(module, selectedKey, [...parentPath, key], selectedField);
      if (!editable) return <tr key={`meta-readonly-${key}`}><td>{currentField?.label || labels[key] || key}</td><td>{value == null ? "" : String(value)}</td></tr>;
      const props = {
        value: value ?? "",
        style: { background: "#fff", minWidth: "100%" },
        onChange: (event) => updateMetaData(event, key, module, parentPath),
      };
      const inputType = currentField?.type || (key === "description" ? "textarea" : "text");
      return <tr key={`meta-edit-${key}`}><td><label>{currentField?.label || labels[key] || key}</label></td><td>{inputType === "textarea" ? <Input.TextArea {...props} /> : <Input {...props} type={inputType} />}</td></tr>;
    });
  };

  const renderMetaData = (data, module) => {
    const selectedRow = activeRow?.[module];
    if (!selectedRow?.key) return null;
    const propertyData = data?.[module]?.propertyData;
    const parent = findProperty(propertyData, selectedRow.key);
    const selectedField = selectedRow.childKey ? findProperty(parent?.value, selectedRow.childKey) : parent;
    if (!selectedField) return null;
    const dataSource = ensureDataSource(selectedField.dataSource, defaultMetaDataFieldsList?.data?.default, selectedRow.childKey || selectedRow.key);
    const sourceName = String(dataSource.metaData?.source || "").toLowerCase();
    const manualDraft = data?.[module]?.header?.status === "Draft" &&
      ["physical", "virtual", ""].includes(dataSource.type || "") &&
      !["labvantage", "calculator"].includes(sourceName);
    const manualSamProperty = module === "sam" && isManualSamEntry(selectedRow.childKey || selectedRow.key, selectedField);
    const edited = editStatus === true && (manualSamProperty || selectedField.edited === true || manualDraft);
    return (
      <div className="col-4 bg-light-gry">
        <div className="row headerRowRight"><div className="col-12" style={{ padding: "6px 0" }}>Property Details</div></div>
        <div className="row colspacing" id="rowEdit"><div className="col-12">
          <table className="table table-bordered table-sm labVantageFieldsTable"><tbody>
            {renderMetadataFields(dataSource.metaData, edited, module, [], selectedField)}
          </tbody></table>
        </div></div>
      </div>
    );
  };

  const renderHeder = (module) => {
    if (module === moduleName) return null;
    return (
      <div className="col-12 header-bar" style={{ marginBottom: "10px", padding: "0px" }}><div className="col-12 content">
        <Header moduleName={module} moduleFieldDataSet={dataSet} editStatus={editStatus} handleHeaderDataUpdate={(data) => handleHeaderDataUpdate(data)} />
      </div></div>
    );
  };
  const renderDescription = (module) => (
    <Description moduleName={module} moduleFieldDataSet={dataSet} editStatus={editStatus} handleHeaderDataUpdate={(data) => handleHeaderDataUpdate(data)} />
  );

  const loopProperties = () => {
    if (!dataSet || Object.keys(dataSet).length === 0) return null;
    const loopModule = [
      { id: "material", label: "Main Material Properties" },
      { id: "absorbent", label: "Absorbent Properties" },
      { id: "mechanical", label: "Mechanical Properties" },
      { id: "sam", label: "SAM Properties" },
      { id: "fiber", label: "Fiber Properties" },
    ];
    const newDataSet = dataSet?.material && Object.keys(dataSet.material).length > 0 ? {
      ...dataSet,
      material: {
        ...dataSet.material,
        propertyData: (Array.isArray(dataSet.material.propertyData) ? dataSet.material.propertyData : []).filter((item) => item?.[Object.keys(item || {})[0]]?.propertyMetaData?.tab === "material" || Object.keys(item || {}).includes("materialSpec")),
      },
      absorbent: {
        header: {},
        propertyData: (Array.isArray(dataSet.material.propertyData) ? dataSet.material.propertyData : []).filter((item) => item?.[Object.keys(item || {})[0]]?.propertyMetaData?.tab === "absorbent"),
      },
      mechanical: {
        header: {},
        propertyData: (Array.isArray(dataSet.material.propertyData) ? dataSet.material.propertyData : []).filter((item) => item?.[Object.keys(item || {})[0]]?.propertyMetaData?.tab === "mechanical"),
      },
    } : dataSet;
    const tabItems = loopModule.map((module, index) => {
      if (!newDataSet[module.id] || !Array.isArray(newDataSet[module.id].propertyData)) return null;
      // Partial related-module shells must not create empty tabs before Fiber.
      if (module.id !== moduleName && newDataSet[module.id].propertyData.length === 0 &&
          Object.keys(newDataSet[module.id].header || {}).length === 0) return null;
      return {
        key: String(index),
        label: module.label,
        children: (
          <div className="hover" data-module={module.id} style={{ display: "flex", flexWrap: "wrap", padding: "0 8px" }}>
            {Object.keys(newDataSet[module.id].header || {}).length > 0 && renderHeder(module.id)}
            {Object.keys(newDataSet[module.id].header || {}).length > 0 && renderDescription(module.id)}
            <div className="col-8 propertyInputRow">{renderGridHeader(module.id)}{renderProperties(newDataSet, module.id)}</div>
            {renderMetaData(newDataSet, module.id)}
            {module.id === "mechanical" && <TestMethods materialId={newDataSet.material?.header?.key} materialName={newDataSet.material?.header?.name} materialDescription={newDataSet.material?.header?.description} />}
          </div>
        ),
      };
    }).filter(Boolean);
    // Standalone SAM/Fiber pages have only key "3"/"4", not default "0".
    // Resolve on every render, including asynchronous data and removed tabs.
    const resolvedActiveTabKey = tabPageRef.current === pageIdentity && tabItems.some((item) => item.key === activeTabKey)
      ? activeTabKey
      : tabItems[0]?.key;
    return <Tabs activeKey={resolvedActiveTabKey} onChange={setActiveTabKey} type="card" items={tabItems} />;
  };

  return (
    <div className="content" style={{ background: "#fff" }}><div className="card-container"><div className="container-fluid headerContainer" id="cc">{loopProperties()}</div></div></div>
  );
}

export default InputGrid;
